import { supabase } from './supabase.js'
import { todayISO } from './format.js'
import { planPayout, previousPayout } from './payout-plan.js'
import { depositFromPayable, reverseDeposit } from './worth.js'

/* ────────────────────────────────────────────────────────────────
   Every write also drops a line in activity_log, so the Log tab is
   the one honest record of what happened, in order.
   ──────────────────────────────────────────────────────────────── */
async function logIt(kind, summary, targetType, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind, summary, target_type: targetType ?? null, target_id: targetId ?? null,
    })
  } catch {
    /* The log is a nice-to-have. Never let it fail the real write. */
  }
}

/* ── Categories ─────────────────────────────────────────────── */

export async function getPayoutCategories() {
  const { data, error } = await supabase
    .from('payout_categories')
    .select('*')
    .order('sort_order')
  if (error) throw error
  return data ?? []
}

/* ── A month of Cashflow ────────────────────────────────────── */

/**
 * Everything the Cashflow tab needs for one month, in two round trips
 * rather than one per payout.
 */
/* One round trip, not two. The month used to fetch its payouts and then, once
   those came back, their payables; on a phone every trip is a visible pause,
   so the payables now ride inside the payouts request. */
export async function getMonth(monthKey) {
  const { data, error } = await supabase
    .from('payouts')
    .select('*, payables(*)')
    .eq('month_key', monthKey)
    .order('sort_order')
    .order('sort_order', { referencedTable: 'payables' })
  if (error) throw error

  const payouts = []
  const payables = []
  for (const row of data ?? []) {
    const { payables: own, ...payout } = row
    payouts.push(payout)
    payables.push(...(own ?? []))
  }
  return { payouts, payables }
}

/** Income received in a month, used for the month header's "In". */
export async function getMonthIncome(monthKey) {
  const from = monthKey + '-01'
  const [y, m] = monthKey.split('-').map(Number)
  const to = new Date(y, m, 0)
  const toISO = monthKey + '-' + String(to.getDate()).padStart(2, '0')

  /* Entries tied to a payout are the salary, and the salary is counted from
     the payout itself. Counting both would double his month. */
  const { data, error } = await supabase
    .from('income_entries')
    .select('amount')
    .is('payout_id', null)
    .gte('received_on', from)
    .lte('received_on', toISO)
  if (error) throw error
  return (data ?? []).reduce((sum, r) => sum + Number(r.amount || 0), 0)
}

/**
 * New payout, pre-filled from the last one.
 *
 * There is no list of defaults to keep up to date. What he edits this month is
 * what shows up next month — delete a line and it stops coming back, add one
 * and it carries. The seeded starter list is only used for the very first
 * payout, when there is nothing yet to copy from.
 *
 * planPayout decides the contents and is tested on its own; this function is
 * only the reads and the writes around it.
 */
export async function addPayout(monthKey, salary) {
  const { data: all, error: e0 } = await supabase
    .from('payouts')
    .select('id, month_key, sort_order')
  if (e0) throw e0

  const n = (all ?? []).filter((p) => p.month_key === monthKey).length + 1
  const prev = previousPayout(all ?? [], null)

  const { data: payout, error: e1 } = await supabase
    .from('payouts')
    .insert({ month_key: monthKey, label: 'Payout ' + n, salary, sort_order: n })
    .select()
    .single()
  if (e1) throw e1

  /* payable_defaults is two things now: the one-time starter list, and the
     subscription records. planPayout knows which is which. */
  const { data: defaults, error: e2 } = await supabase
    .from('payable_defaults')
    .select('*')
    .order('sort_order')
  if (e2) throw e2

  let prevPayables = []
  let chargedIds = []
  if (prev) {
    const { data: rows, error: e3 } = await supabase
      .from('payables')
      .select('*')
      .eq('payout_id', prev.id)
      .order('sort_order')
    if (e3) throw e3
    prevPayables = rows ?? []

    /* An installment line reloads from its plan, so copying it would charge
       him twice for the same month. */
    if (prevPayables.length) {
      const { data: charges } = await supabase
        .from('installment_charges')
        .select('payable_id')
        .in('payable_id', prevPayables.map((p) => p.id))
      chargedIds = (charges ?? []).map((c) => c.payable_id)
    }
  }

  const planned = planPayout({
    prevPayables,
    chargedIds,
    defaults: defaults ?? [],
    monthKey,
    n,
  })

  if (planned.length) {
    const { error: e4 } = await supabase.from('payables').insert(
      planned.map((row, i) => ({
        ...row,
        payout_id: payout.id,
        status: 'Pending',
        sort_order: i,
      })),
    )
    if (e4) throw e4
  }

  /* This month's installment charges, once per month rather than per payout. */
  let installments = 0
  if (n === 1) installments = await loadInstallmentCharges(payout.id, monthKey, planned.length)

  await logIt('payout.add', payout.label + ' added', 'payout', payout.id)
  return { ...payout, installments, copiedFrom: prev ? planned.length : 0 }
}

/**
 * Charges falling due this month, from active plans, not already attached to
 * a payable. The charge keeps the payable's id, which is what lets ticking it
 * paid move the plan to 8 of 12 without matching on anything by name.
 */
async function loadInstallmentCharges(payoutId, monthKey, startSort) {
  const [y, m] = monthKey.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  const from = monthKey + '-01'
  const to = monthKey + '-' + String(last).padStart(2, '0')

  const { data: charges, error } = await supabase
    .from('installment_charges')
    .select('id, plan_id, seq, amount, due_date')
    .is('payable_id', null)
    .eq('status', 'Pending')
    .gte('due_date', from)
    .lte('due_date', to)
  if (error || !charges?.length) return 0

  const planIds = Array.from(new Set(charges.map((c) => c.plan_id)))
  const { data: plans } = await supabase
    .from('installment_plans')
    .select('id, name, term_count, status')
    .in('id', planIds)
    .eq('status', 'Active')

  const byId = Object.fromEntries((plans ?? []).map((p) => [p.id, p]))
  const live = charges.filter((c) => byId[c.plan_id])
  if (!live.length) return 0

  const { data: made, error: e2 } = await supabase
    .from('payables')
    .insert(live.map((c, i) => ({
      payout_id: payoutId,
      name: byId[c.plan_id].name + ' ' + c.seq + '/' + byId[c.plan_id].term_count,
      amount: c.amount,
      status: 'Pending',
      sort_order: startSort + i,
    })))
    .select()
  if (e2 || !made) return 0

  await Promise.all(
    made.map((pay, i) =>
      supabase.from('installment_charges').update({ payable_id: pay.id }).eq('id', live[i].id),
    ),
  )
  return made.length
}

export async function updatePayout(id, patch) {
  const { error } = await supabase.from('payouts').update(patch).eq('id', id)
  if (error) throw error
  await logIt('payout.edit', 'Payout updated', 'payout', id)
}

export async function deletePayout(id) {
  const { error } = await supabase.from('payouts').delete().eq('id', id)
  if (error) throw error
  await logIt('payout.delete', 'Payout deleted', 'payout', id)
}

/* ── Payables ───────────────────────────────────────────────── */

export async function addPayable({
  payoutId, name, amount, categoryId, buildId, toAccountId, sortOrder,
}) {
  const { data, error } = await supabase
    .from('payables')
    .insert({
      payout_id: payoutId,
      name,
      amount,
      category_id: categoryId ?? null,
      build_id: buildId ?? null,
      to_account_id: toAccountId ?? null,
      status: 'Pending',
      sort_order: sortOrder ?? 999,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('payable.add', name + ' added', 'payable', data.id)
  return data
}

/**
 * Amount and name changes are recorded, so Reports can show the grocery
 * line creeping up. He never has to enter anything extra for this.
 */
export async function updatePayable(id, patch, previous) {
  const { error } = await supabase.from('payables').update(patch).eq('id', id)
  if (error) throw error

  if (previous) {
    const tracked = ['amount', 'name', 'category_id']
    const rows = tracked
      .filter((f) => f in patch && String(patch[f]) !== String(previous[f]))
      .map((f) => ({
        payable_id: id,
        field: f,
        old_value: previous[f] == null ? null : String(previous[f]),
        new_value: patch[f] == null ? null : String(patch[f]),
      }))
    if (rows.length) await supabase.from('payable_history').insert(rows)
  }
}

export async function deletePayable(id) {
  const { error } = await supabase.from('payables').delete().eq('id', id)
  if (error) throw error
}

/**
 * The one piece of real behaviour in this file.
 *
 * Ticking a payable paid is the same event as paying into its build, so
 * marking it writes the build payment too. Unticking removes that payment
 * again. One record, two views, and they cannot disagree.
 *
 * Only ACTIVE builds receive payments. A finished build stops accepting.
 */
export async function setPayableStatus(payable, paid, payoutLabel) {
  const status = paid ? 'Paid' : 'Pending'
  const { error } = await supabase.from('payables').update({ status }).eq('id', payable.id)
  if (error) throw error

  /* Same one-event rule as builds: if this payable is an installment charge,
     ticking it here is what moves the plan. Never both entered by hand. */
  const plan = await syncInstallmentCharge(payable, paid)

  /* A push to savings is the same rule again. The tick is the event: it moves
     the money into the account and unticking takes it back out. */
  let pushed = null
  if (payable.to_account_id) {
    pushed = paid
      ? await depositFromPayable(payable, payoutLabel)
      : await reverseDeposit(payable.id)
  }

  if (!payable.build_id) {
    await logIt(
      paid ? 'payable.paid' : 'payable.unpaid',
      payable.name + (paid ? ' paid' : ' set back to pending'),
      'payable', payable.id,
    )
    return { buildTouched: false, plan, pushed }
  }

  if (paid) {
    const { data: build, error: e1 } = await supabase
      .from('builds')
      .select('id, name, status')
      .eq('id', payable.build_id)
      .single()
    if (e1) throw e1

    if (build?.status !== 'Active') {
      await logIt('payable.paid', payable.name + ' paid', 'payable', payable.id)
      return { buildTouched: false, skippedBuild: build?.name ?? null, plan, pushed }
    }

    const { error: e2 } = await supabase.from('build_payments').insert({
      build_id: build.id,
      paid_on: todayISO(),
      amount: payable.amount,
      note: payoutLabel ?? null,
      source_payable_id: payable.id,
    })
    if (e2) throw e2
    await logIt('build.payment', payable.amount + ' paid into ' + build.name, 'build', build.id)
    return { buildTouched: true, plan, pushed }
  }

  const { error: e3 } = await supabase
    .from('build_payments')
    .delete()
    .eq('source_payable_id', payable.id)
  if (e3) throw e3
  await logIt('payable.unpaid', payable.name + ' set back to pending', 'payable', payable.id)
  return { buildTouched: true, plan, pushed }
}

/**
 * Moves the plan when its charge is ticked in Cashflow, and closes the plan on
 * the last one. Returns what happened so the screen can say so.
 */
async function syncInstallmentCharge(payable, paid) {
  const { data: charge } = await supabase
    .from('installment_charges')
    .select('id, plan_id, seq')
    .eq('payable_id', payable.id)
    .maybeSingle()
  if (!charge) return null

  await supabase
    .from('installment_charges')
    .update({ status: paid ? 'Paid' : 'Pending', paid_on: paid ? todayISO() : null })
    .eq('id', charge.id)

  const { data: plan } = await supabase
    .from('installment_plans')
    .select('id, name, term_count, status')
    .eq('id', charge.plan_id)
    .single()
  if (!plan) return null

  const { data: siblings } = await supabase
    .from('installment_charges')
    .select('status')
    .eq('plan_id', plan.id)
  const paidCount = (siblings ?? []).filter((c) => c.status === 'Paid').length
  const finished = paidCount >= plan.term_count

  if (finished && plan.status !== 'Closed') {
    await supabase.from('installment_plans')
      .update({ status: 'Closed', closed_at: todayISO() }).eq('id', plan.id)
  } else if (!finished && plan.status === 'Closed') {
    await supabase.from('installment_plans')
      .update({ status: 'Active', closed_at: null }).eq('id', plan.id)
  }

  return { name: plan.name, paidCount, term: plan.term_count, closed: finished && paid }
}

/* ── Builds ─────────────────────────────────────────────────── */

export async function getBuilds() {
  const { data: builds, error } = await supabase
    .from('builds')
    .select('*')
    .order('status')
    .order('sort_order')
  if (error) throw error

  const ids = (builds ?? []).map((b) => b.id)
  let payments = []
  if (ids.length) {
    const { data, error: e2 } = await supabase
      .from('build_payments')
      .select('*')
      .in('build_id', ids)
      .order('paid_on', { ascending: false })
    if (e2) throw e2
    payments = data ?? []
  }
  return { builds: builds ?? [], payments }
}

export async function addBuild({ name, target }) {
  const { data, error } = await supabase
    .from('builds')
    .insert({ name, target, status: 'Active' })
    .select()
    .single()
  if (error) throw error
  await logIt('build.add', name + ' started', 'build', data.id)
  return data
}

export async function updateBuild(id, patch) {
  const { error } = await supabase.from('builds').update(patch).eq('id', id)
  if (error) throw error
}

/**
 * Every line he has ever written, so typing one again completes itself.
 *
 * This is the memory he does want: it suggests, it never inserts. Nothing here
 * puts a line in a payout — it only offers what a name was worth last time, so
 * he is not retyping "Grocery 11000 MOB" every month. The same names are what
 * Reports follows over time, which is why they are worth keeping tidy.
 */
export async function payableSuggestions(limit = 400) {
  const { data, error } = await supabase
    .from('payables')
    .select('name, amount, category_id, build_id, to_account_id, created_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error

  const seen = new Map()
  for (const p of data ?? []) {
    const name = (p.name || '').trim()
    if (!name) continue
    const key = name.toLowerCase()
    const found = seen.get(key)
    if (found) {
      found.uses += 1
      continue
    }
    /* First time through is the most recent row, so that is the one whose
       amount and category are worth offering back. */
    seen.set(key, {
      key,
      name,
      amount: Number(p.amount || 0),
      categoryId: p.category_id ?? null,
      buildId: p.build_id ?? null,
      toAccountId: p.to_account_id ?? null,
      uses: 1,
    })
  }

  return [...seen.values()].sort((a, b) => b.uses - a.uses || a.name.localeCompare(b.name))
}

/** The remembered line for exactly this name, or null. Case does not matter. */
export function suggestionFor(suggestions, typed) {
  const key = (typed || '').trim().toLowerCase()
  if (!key) return null
  return suggestions.find((s) => s.key === key) ?? null
}

/* A Log line names a payout or a payable but never the month it sits in, and
   Cashflow loads one month at a time. This is the single query that bridges
   the two, so a jump can land on the right month before it selects anything. */
export async function findPayoutLocation(type, id) {
  if (type === 'payout') {
    const { data, error } = await supabase
      .from('payouts').select('id, month_key').eq('id', id).maybeSingle()
    if (error) throw error
    return data ? { monthKey: data.month_key, payoutId: data.id, payableId: null } : null
  }
  const { data, error } = await supabase
    .from('payables').select('id, payout_id, payouts(month_key)').eq('id', id).maybeSingle()
  if (error) throw error
  const monthKey = data?.payouts?.month_key
  return monthKey ? { monthKey, payoutId: data.payout_id, payableId: data.id } : null
}
